import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'md' | 'lg';

/**
 * Every call to action on this page was previously a bare <a> carrying its own
 * copy-pasted utility classes, which had drifted apart on padding, radius and
 * hover behaviour. This is the single source of truth for CTA styling.
 *
 * Renders an <a> when given an href, a <button> otherwise.
 */
interface BaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: React.ReactNode;
}

type AnchorProps = BaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & { href: string };

type NativeButtonProps = BaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { href?: undefined };

export type ButtonProps = AnchorProps | NativeButtonProps;

const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold rounded-xl ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-200 ' +
  'motion-safe:hover:-translate-y-0.5 active:translate-y-0 whitespace-nowrap';

const VARIANTS: Record<ButtonVariant, string> = {
  // The single highest-intent action on any given screen.
  primary:
    'bg-esf-blue text-white hover:bg-esf-blue-glow ' +
    'shadow-[0_0_28px_-10px_rgba(59,130,246,0.55)] hover:shadow-[0_0_36px_-8px_rgba(59,130,246,0.7)]',
  // Present but deliberately quieter — sits next to a primary without competing.
  secondary:
    'bg-white/[0.04] text-esf-white border border-white/10 ' +
    'hover:bg-white/[0.08] hover:border-white/20 backdrop-blur-sm',
  // Text-level action for tertiary paths.
  ghost: 'text-esf-gray-300 hover:text-esf-white',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'px-5 py-2.5 text-body-sm',
  lg: 'px-8 py-4 text-body-lg',
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}) => {
  const classes = [
    BASE,
    VARIANTS[variant],
    variant === 'ghost' ? '' : SIZES[size],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (typeof (rest as AnchorProps).href === 'string') {
    const anchorProps = rest as Omit<AnchorProps, keyof BaseProps>;
    const isExternal = /^https?:\/\//.test(anchorProps.href);
    return (
      <a
        {...anchorProps}
        className={classes}
        {...(isExternal ? { rel: anchorProps.rel ?? 'noopener' } : {})}
      >
        {children}
      </a>
    );
  }

  const buttonProps = rest as Omit<NativeButtonProps, keyof BaseProps>;
  return (
    <button type="button" {...buttonProps} className={classes}>
      {children}
    </button>
  );
};

export default Button;
